import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  validateFileForBucket,
  generateUniqueFileNameWithOriginal,
} from "@/utils/fileOperations";
import {
  getStorageUrl,
  extractStoragePath,
  isStorageUrl,
  normalizeToPath,
} from "@/utils/storageUtils";

interface UseImageUploadProps {
  bucketName: string;
  onPathChange?: (path: string) => void;
  onUrlChange?: (url: string) => void;
  onUploadStateChange?: (uploading: boolean) => void;
  currentPath?: string;
  currentUrl?: string;
}

export const useImageUpload = ({
  bucketName,
  onPathChange,
  onUrlChange,
  onUploadStateChange,
  currentPath,
  currentUrl,
}: UseImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const { toast } = useToast();

  // Call onUploadStateChange whenever uploading state changes
  useEffect(() => {
    if (onUploadStateChange) {
      onUploadStateChange(uploading);
    }
  }, [uploading, onUploadStateChange]);

  // Determine current path and URL for display
  const effectivePath = currentPath || normalizeToPath(currentUrl || "");
  const displayUrl = effectivePath
    ? getStorageUrl(bucketName, effectivePath)
    : currentUrl || "";

  const deleteFileFromStorage = async (filePath: string): Promise<boolean> => {
    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (error) {
        console.error(`Error deleting file from ${bucketName}:`, error);
        return false;
      }

      console.log(
        `Successfully deleted file: ${filePath} from bucket: ${bucketName}`
      );
      return true;
    } catch (error) {
      console.error(`Failed to delete file from ${bucketName}:`, error);
      return false;
    }
  };

  const uploadFile = async (file: File) => {
    if (!file) return;

    const validation = validateFileForBucket(file, bucketName);
    if (!validation.isValid) {
      toast({
        title: "Invalid file type",
        description: validation.error,
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);

      // Delete existing file before uploading new one
      if (effectivePath) {
        await deleteFileFromStorage(effectivePath);
      }

      // Use the new filename generation that preserves original name
      const fileName = generateUniqueFileNameWithOriginal(file);

      console.log("Uploading file:", fileName, "Original:", file.name);

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        throw error;
      }

      console.log("File uploaded successfully:", data);

      // Store only the path, not the full URL
      if (onPathChange) {
        onPathChange(`/${fileName}`);
      }

      // For backward compatibility with components expecting URLs
      if (onUrlChange) {
        onUrlChange(getStorageUrl(bucketName, fileName));
      }

      toast({
        title: "Success",
        description: "File uploaded successfully!",
      });
    } catch (error: any) {
      console.error("Upload failed:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload file",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const clearImage = async () => {
    if (effectivePath) {
      const deleted = await deleteFileFromStorage(effectivePath);
      if (deleted) {
        if (onPathChange) onPathChange("");
        if (onUrlChange) onUrlChange("");
        toast({
          title: "Success",
          description: "File deleted successfully!",
        });
      } else {
        toast({
          title: "Warning",
          description:
            "File reference cleared, but file deletion may have failed.",
          variant: "destructive",
        });
        if (onPathChange) onPathChange("");
        if (onUrlChange) onUrlChange("");
      }
    } else {
      if (onPathChange) onPathChange("");
      if (onUrlChange) onUrlChange("");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Handle URL input - convert URLs to paths, or use paths directly
  const handleUrlInput = (input: string) => {
    if (!input) {
      if (onPathChange) onPathChange("");
      if (onUrlChange) onUrlChange("");
      return;
    }

    // If it's a Supabase storage URL, extract the path
    if (isStorageUrl(input)) {
      const path = extractStoragePath(input);
      if (path) {
        if (onPathChange) onPathChange(path);
        if (onUrlChange) onUrlChange(input);
      } else {
        toast({
          title: "Invalid URL",
          description: "Could not extract file path from the provided URL",
          variant: "destructive",
        });
      }
    } else {
      // Assume it's already a path or an external URL
      if (onPathChange) onPathChange(input);
      if (onUrlChange) onUrlChange(input);
    }
  };

  return {
    uploading,
    dragOver,
    currentUrl: displayUrl,
    uploadFile,
    clearImage,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleUrlInput,
  };
};
